import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Camera, X, Radio, CheckCircle } from 'lucide-react'
import {
  createAnimal, updateAnimal, getAnimal, getBreeds,
  getPaddocks, getHerds, getAllCattle, getOwnerSegments
} from '../../services/api'
import useUnsavedChanges from '../../hooks/useUnsavedChanges'
import useSubmitLock from '../../hooks/useSubmitLock'

export default function CattleForm({ ranchId }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isEditing = Boolean(id)
  const queryClient = useQueryClient()

  // Check if RFID was passed from scan
  const scannedRfid = location.state?.scannedRfid

  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    rfid_tag: '',
    tag_id: '',
    local_name: '',
    breed_id: '',
    breed_purity: '',
    breed_notes: '',
    sex: '',
    birth_date: '',
    acquisition_date: '',
    acquisition_type: 'born_on_farm',
    acquisition_price: '',
    category: 'calf',
    status: 'active',
    current_paddock_id: '',
    herd: 'Main',
    owner_segment: '',
    owner_segment_id: '',
    dam_id: '',
    sire_id: '',
    notes: '',
  })
  const [photo, setPhoto] = useState(null)
  const [error, setError] = useState('')
  // "Other" mode for the owner-segment picker: user is typing a brand-new owner
  // name rather than selecting an existing one.
  const [otherOwner, setOtherOwner] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  useUnsavedChanges(isDirty)

  // Fetch existing animal if editing
  const { data: existingAnimal } = useQuery({
    queryKey: ['animal', id],
    queryFn: () => getAnimal(id),
    enabled: isEditing,
  })

  // Fetch reference data
  const { data: breeds } = useQuery({
    queryKey: ['breeds'],
    queryFn: getBreeds,
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const { data: herds } = useQuery({
    queryKey: ['herds'],
    queryFn: getHerds,
  })

  // All owner segments for the ranch (incl. seeded-but-empty) so registration
  // selects an existing owner instead of re-typing it (prevents spelling drift).
  const { data: ownerSegments } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }),
  })

  // Fetch cattle for dam/sire selection
  const { data: cattleList } = useQuery({
    queryKey: ['cattle', ranchId],
    queryFn: () => getAllCattle(ranchId, {}),
  })

  // Filter for potential dams (female) and sires (male)
  const potentialDams = (cattleList || []).filter(c =>
    c.sex === 'female' && c.id !== id
  )

  const potentialSires = (cattleList || []).filter(c =>
    c.sex === 'male' && c.id !== id
  )

  // Populate form when editing
  useEffect(() => {
    if (existingAnimal) {
      setFormData({
        ranch_id: existingAnimal.ranch_id || ranchId,
        rfid_tag: existingAnimal.rfid_tag || '',
        tag_id: existingAnimal.tag_id || '',
        local_name: existingAnimal.local_name || '',
        breed_id: existingAnimal.breed_id || '',
        breed_purity: existingAnimal.breed_purity || '',
        breed_notes: existingAnimal.breed_notes || '',
        sex: existingAnimal.sex || '',
        birth_date: existingAnimal.birth_date?.split('T')[0] || '',
        acquisition_date: existingAnimal.acquisition_date?.split('T')[0] || '',
        acquisition_type: existingAnimal.acquisition_type || 'born_on_farm',
        acquisition_price: existingAnimal.acquisition_price || '',
        category: existingAnimal.category || 'calf',
        status: existingAnimal.status || 'active',
        current_paddock_id: existingAnimal.current_paddock_id || '',
        herd: existingAnimal.herd || 'Main',
        owner_segment: existingAnimal.owner_segment || '',
        owner_segment_id: existingAnimal.owner_segment_id || '',
        dam_id: existingAnimal.dam_id || '',
        sire_id: existingAnimal.sire_id || '',
        notes: existingAnimal.notes || '',
      })
      // Legacy animals with an owner name but no linked segment id start in
      // "Other" mode so the existing name stays editable.
      setOtherOwner(Boolean(existingAnimal.owner_segment) && !existingAnimal.owner_segment_id)
    }
  }, [existingAnimal])

  // Pre-populate RFID from scan
  useEffect(() => {
    if (scannedRfid && !isEditing) {
      setFormData(prev => ({ ...prev, rfid_tag: scannedRfid }))
    }
  }, [scannedRfid, isEditing])

  // Helper to format API error messages
  const formatError = (err) => {
    const detail = err.response?.data?.detail
    if (!detail) return 'An error occurred'
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // Pydantic validation errors are an array of objects
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
    }
    return 'An error occurred'
  }

  const { locked, lock, unlock } = useSubmitLock()

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: createAnimal,
    onSuccess: () => {
      queryClient.invalidateQueries(['cattle'])
      navigate('/cattle')
    },
    onError: (err) => setError(formatError(err)),
    onSettled: unlock,
  })

  const updateMutation = useMutation({
    mutationFn: (data) => updateAnimal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cattle'])
      queryClient.invalidateQueries(['animal', id])
      navigate(`/cattle/${id}`)
    },
    onError: (err) => setError(formatError(err)),
    onSettled: unlock,
  })

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setPhoto(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!lock()) return

    // Build payload, removing empty strings
    const payload = { ...formData }
    Object.keys(payload).forEach(key => {
      if (payload[key] === '') {
        delete payload[key]
      }
    })

    // Add photo if present
    if (photo) {
      payload.photo_url = photo
    }

    if (isEditing) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  const isLoading = locked || createMutation.isPending || updateMutation.isPending

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Animal' : 'Register New Animal'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update animal information' : 'Add a new animal to your herd'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} onChange={() => setIsDirty(true)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RFID Tag *
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={formData.rfid_tag}
                  onChange={(e) => setFormData({ ...formData, rfid_tag: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    scannedRfid ? 'border-green-500 bg-green-50 pr-10' : 'border-gray-300'
                  }`}
                  placeholder="UG-2026-001"
                />
                {scannedRfid && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-600">
                    <Radio className="h-4 w-4" />
                    <CheckCircle className="h-4 w-4" />
                  </div>
                )}
              </div>
              {scannedRfid && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Scanned from EID reader
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag ID
              </label>
              <input
                type="text"
                value={formData.tag_id}
                onChange={(e) => setFormData({ ...formData, tag_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., 001, A-15"
              />
              <p className="text-xs text-gray-500 mt-1">
                Visual ID number (simpler than RFID)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local Name
              </label>
              <input
                type="text"
                value={formData.local_name}
                onChange={(e) => setFormData({ ...formData, local_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., Spotted One"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Breed *
              </label>
              <select
                required
                value={formData.breed_id}
                onChange={(e) => setFormData({ ...formData, breed_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select breed</option>
                {breeds?.map(breed => (
                  <option key={breed.id} value={breed.id}>{breed.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Breed Purity
              </label>
              <select
                value={formData.breed_purity}
                onChange={(e) => setFormData({ ...formData, breed_purity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Unknown</option>
                <option value="purebred">Purebred</option>
                <option value="crossbred">Crossbred</option>
                <option value="grade">Grade</option>
                <option value="f1">F1 Cross</option>
                <option value="f2">F2 Cross</option>
                <option value="three_quarter">3/4 Breed</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Genetic purity classification
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Breed Notes
              </label>
              <textarea
                value={formData.breed_notes}
                onChange={(e) => setFormData({ ...formData, breed_notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows={2}
                placeholder="e.g., Dam is Ankole, Sire is Boran..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sex *
              </label>
              <select
                required
                value={formData.sex}
                onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select sex</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="calf">Calf</option>
                <option value="weaner">Weaner</option>
                <option value="heifer">Heifer</option>
                <option value="cow">Cow</option>
                <option value="bull">Bull</option>
                <option value="steer">Steer</option>
                <option value="oxen">Oxen</option>
              </select>
            </div>
          </div>
        </div>

        {/* Herd & Location */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Herd & Location</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Herd *
              </label>
              <select
                required
                value={formData.herd}
                onChange={(e) => setFormData({ ...formData, herd: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {herds?.map(herd => (
                  <option key={herd.value} value={herd.value}>{herd.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Which herd does this animal belong to?
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner / Segment
              </label>
              <select
                value={otherOwner ? '__other__' : (formData.owner_segment_id || '')}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === '__other__') {
                    setOtherOwner(true)
                    setFormData({ ...formData, owner_segment_id: '' })
                  } else if (v === '') {
                    setOtherOwner(false)
                    setFormData({ ...formData, owner_segment_id: '', owner_segment: '' })
                  } else {
                    const seg = (ownerSegments || []).find(s => s.id === v)
                    setOtherOwner(false)
                    setFormData({ ...formData, owner_segment_id: v, owner_segment: seg?.name || '' })
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select owner / segment</option>
                {(ownerSegments || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
                <option value="__other__">Other (add new)…</option>
              </select>
              {otherOwner && (
                <input
                  type="text"
                  value={formData.owner_segment}
                  onChange={(e) => setFormData({ ...formData, owner_segment: e.target.value })}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="New owner / segment name"
                  autoFocus
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                Optional: pick an existing owner, or choose “Other” to add a new one.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Paddock
              </label>
              <select
                value={formData.current_paddock_id}
                onChange={(e) => setFormData({ ...formData, current_paddock_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Not assigned</option>
                {paddocks?.map(paddock => (
                  <option key={paddock.id} value={paddock.id}>
                    {paddock.code} - {paddock.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="sold">Sold</option>
                <option value="deceased">Deceased</option>
                <option value="missing">Missing</option>
                <option value="culled">Culled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lineage */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lineage</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dam (Mother)
              </label>
              <select
                value={formData.dam_id}
                onChange={(e) => setFormData({ ...formData, dam_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Unknown / Not in system</option>
                {potentialDams.map(dam => (
                  <option key={dam.id} value={dam.id}>
                    {dam.local_name || dam.rfid_tag} ({dam.category})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sire (Father)
              </label>
              <select
                value={formData.sire_id}
                onChange={(e) => setFormData({ ...formData, sire_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Unknown / Not in system</option>
                {potentialSires.map(sire => (
                  <option key={sire.id} value={sire.id}>
                    {sire.local_name || sire.rfid_tag} ({sire.category})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Acquisition */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Acquisition</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Acquisition Type *
              </label>
              <select
                required
                value={formData.acquisition_type}
                onChange={(e) => setFormData({ ...formData, acquisition_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="born_on_farm">Born on Farm</option>
                <option value="purchased">Purchased</option>
                <option value="gift">Gift</option>
                <option value="inherited">Inherited</option>
                <option value="exchange">Exchange</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Acquisition Date
              </label>
              <input
                type="date"
                value={formData.acquisition_date}
                onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Acquisition Price (UGX)
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.acquisition_price ? Number(formData.acquisition_price).toLocaleString('en-US') : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '')
                    setFormData({ ...formData, acquisition_price: rawValue })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 1,500,000"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Purchase price in Uganda Shillings
              </p>
            </div>
          </div>
        </div>

        {/* Photo */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Photo</h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            {photo ? (
              <div className="relative inline-block">
                <img src={photo} alt="Animal" className="h-32 w-32 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Camera className="h-10 w-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to upload photo</span>
                </label>
              </>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
          
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            rows={3}
            placeholder="Any additional notes about this animal..."
          />
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {isLoading ? 'Saving...' : (isEditing ? 'Update Animal' : 'Register Animal')}
          </button>
        </div>
      </form>
    </div>
  )
}
